import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const CohortType = z.enum(['position', 'class'])
const Cohort = z
  .object({
    id: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().min(1),
    organizationId: z.string(),
    type: CohortType,
    isActive: z.boolean(),
    personIds: z.array(z.string()).optional(),
    challengeDisclaimer: z.string().optional(),
  })
  .passthrough()
const CohortListResponse = z.object({ data: z.array(Cohort) }).passthrough()
const ErrorResponse = z
  .object({
    error: z.string(),
    errorCode: z
      .enum([
        'organizationIdRequired',
        'badRequest',
        'cohortClosed',
        'cohortNotFound',
        'cohortNotInOrganization',
        'cohortIdRequired',
        'assignmentNotFound',
        'assignmentAlreadySent',
        'invalidAssignmentScope',
        'invalidAssignmentStatus',
        'organizationNotFound',
        'invalidOrganizationType',
        'alreadyJoinedPublicAssignment',
      ])
      .optional(),
  })
  .passthrough()
const CreateCohortRequest = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().min(1).optional(),
    type: CohortType,
    isActive: z.boolean(),
    personIds: z.array(z.string()).optional(),
    challengeDisclaimer: z.string().optional(),
  })
  .passthrough()
const AssignmentDetailsStatus = z.enum([
  'sent',
  'started',
  'submitted',
  'forfeited',
])
const AssignmentDetailsQueryEntry = z
  .object({
    query: z.string(),
    resultCount: z.number().optional(),
    errorCode: z.string().optional(),
  })
  .passthrough()
const AssignmentDetailsQueries = z
  .object({
    totalCount: z.number(),
    last20: z.array(AssignmentDetailsQueryEntry),
  })
  .passthrough()
const AssignmentDetails = z
  .object({
    personId: z.string(),
    assignmentId: z.string(),
    taskId: z.string(),
    status: AssignmentDetailsStatus,
    challengeId: z.string().optional(),
    grade: z.number().optional(),
    feedback: z.string().optional(),
    solution: z.string().optional(),
    workTime: z.number().optional(),
    startDate: z.string().datetime({ offset: true }).optional(),
    submitDate: z.string().datetime({ offset: true }).optional(),
    isPublicAssignment: z.boolean().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    cohortName: z.string().optional(),
    charts: z
      .record(
        z
          .object({
            id: z.string().optional(),
            chartType: z.enum(['table', 'line', 'bar', 'pie', 'scatter']),
            xAxis: z.string().optional(),
            yAxis: z.string().optional(),
            query: z.string(),
            data: z.array(z.object({}).partial().passthrough()).optional(),
            showTrendLine: z.boolean().optional(),
          })
          .passthrough()
      )
      .optional(),
    queries: AssignmentDetailsQueries.optional(),
  })
  .passthrough()
const AssignmentStatus = z.enum(['inactive', 'active'])
const AssignmentSettings = z
  .object({ releaseFeedbacks: z.boolean() })
  .partial()
  .passthrough()
const Assignment = z
  .object({
    id: z.string(),
    cohortId: z.string(),
    taskId: z.string(),
    ownerId: z.string(),
    organizationId: z.string(),
    timeLimit: z.number().optional(),
    startDate: z.string().datetime({ offset: true }).optional(),
    dueDate: z.string().datetime({ offset: true }).optional(),
    overdueDate: z.string().datetime({ offset: true }).optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    personIds: z.array(z.string()),
    status: AssignmentStatus,
    submissions: z.number().default(0),
    settings: AssignmentSettings.optional(),
    linkToken: z.string().optional(),
  })
  .passthrough()
const CohortResponse = z
  .object({
    data: Cohort,
    assignmentsDetails: z.array(AssignmentDetails).optional(),
    publicAssignments: z.array(Assignment).optional(),
  })
  .passthrough()
const CohortPersonInput = z
  .object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
  })
  .passthrough()
const AddPeopleToCohortRequest = z
  .object({
    cohortId: z.string().min(1),
    people: z.array(CohortPersonInput).min(1),
  })
  .passthrough()
const AddPeopleToCohortResponse = z
  .object({ data: Cohort, createdPersonIds: z.array(z.string()).optional() })
  .passthrough()
const RemovePeopleFromCohortRequest = z
  .object({
    cohortId: z.string().min(1),
    peopleIds: z.array(z.string()).min(1),
  })
  .passthrough()
const RemovePeopleFromCohortResponse = z.object({ data: Cohort }).passthrough()
const UpdateCohortRequest = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().min(1).optional(),
    organizationId: z.string(),
    type: CohortType,
    isActive: z.boolean(),
    personIds: z.array(z.string()).optional(),
    challengeDisclaimer: z.string().optional(),
  })
  .passthrough()
const OrganizationType = z.enum(['company', 'school'])
const queryFilters = z
  .object({ type: OrganizationType, active: z.boolean() })
  .partial()
  .passthrough()
  .optional()
const OrganizationSettings = z
  .object({
    invitationEmails: z.boolean(),
    submissionEmails: z.boolean(),
    feedbackLanguage: z.enum(['en', 'he', 'es', 'fr', 'de']),
  })
  .partial()
  .passthrough()
const OrganizationAdminSettings = z
  .object({ allowPublicLinks: z.boolean() })
  .partial()
  .passthrough()
const Organization = z
  .object({
    id: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    active: z.boolean(),
    logoURL: z.string().optional(),
    type: OrganizationType,
    availableTaskIds: z.array(z.string()).optional(),
    settings: OrganizationSettings.optional(),
    adminSettings: OrganizationAdminSettings.optional(),
  })
  .passthrough()
const OrganizationListResponse = z
  .object({ data: z.array(Organization) })
  .passthrough()
const CreateOrganizationRequest = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    active: z.boolean(),
    logoURL: z.string().optional(),
    type: OrganizationType,
    availableTaskIds: z.array(z.string()).optional(),
  })
  .passthrough()
const OrganizationResponse = z.object({ data: Organization }).passthrough()
const OrganizationSearchResult = z
  .object({
    id: z.string(),
    name: z.string(),
    type: OrganizationType,
    active: z.boolean(),
  })
  .partial()
  .passthrough()
const UpdateOrganizationRequest = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    active: z.boolean(),
    logoURL: z.string().optional(),
    type: OrganizationType,
    availableTaskIds: z.array(z.string()).optional(),
    settings: OrganizationSettings.optional(),
    adminSettings: OrganizationAdminSettings.optional(),
  })
  .passthrough()
const UpdateOrganizationSettingsRequest = z
  .object({ settings: OrganizationSettings })
  .passthrough()
const AssignmentListResponse = z
  .object({
    assignments: z.array(Assignment),
    organizations: z.array(Organization),
  })
  .passthrough()
const CreateAssignmentRequest = z
  .object({
    cohortId: z.string(),
    taskId: z.string(),
    timeLimit: z.number().optional(),
    startDate: z.string().datetime({ offset: true }).optional(),
    dueDate: z.string().datetime({ offset: true }).optional(),
    overdueDate: z.string().datetime({ offset: true }).optional(),
    personIds: z.array(z.string()),
    settings: AssignmentSettings.optional(),
    public: z.boolean().optional(),
  })
  .passthrough()
const AssignmentResponse = z.object({ data: Assignment }).passthrough()
const UpdateAssignmentRequest = z
  .object({
    cohortId: z.string(),
    taskId: z.string(),
    ownerId: z.string(),
    timeLimit: z.number().optional(),
    startDate: z.string().datetime({ offset: true }).optional(),
    dueDate: z.string().datetime({ offset: true }).optional(),
    overdueDate: z.string().datetime({ offset: true }).optional(),
    personIds: z.array(z.string()),
    submissions: z.number().optional().default(0),
    settings: AssignmentSettings.optional(),
    linkToken: z.string().optional(),
  })
  .passthrough()
const SetAssignmentStatusRequest = z
  .object({ status: AssignmentStatus })
  .passthrough()
const AssignmentDisclaimerResponse = z
  .object({ assignmentDisclaimer: z.string() })
  .passthrough()
const VerifyLinkTokenResponse = z
  .object({
    valid: z.boolean(),
    organizationId: z.string().optional(),
    organizationName: z.string().optional(),
    assignmentId: z.string().optional(),
    taskName: z.string().optional(),
    cohortId: z.string().optional(),
  })
  .passthrough()
const CohortWithStats = z
  .object({
    cohort: Cohort,
    personCount: z.number(),
    assignmentCount: z.number(),
    submissionCount: z.number(),
  })
  .passthrough()
const PartnerDashboardResponse = z
  .object({
    recentAssignments: z.array(AssignmentDetails),
    cohortsWithStats: z.array(CohortWithStats),
  })
  .passthrough()
const AddPublicPersonRequest = z
  .object({
    assignmentId: z.string(),
    cohortId: z.string(),
    personId: z.string(),
  })
  .passthrough()
const AddPublicPersonResponse = z
  .object({ success: z.boolean() })
  .passthrough()
const AdminCleanupPersonRequest = z
  .object({ personId: z.string(), dryRun: z.boolean() })
  .passthrough()
const AdminCleanupPersonResponse = z
  .object({
    assignments: z.array(z.object({}).partial().passthrough()),
    cohorts: z.array(z.object({}).partial().passthrough()),
    removedFromAssignments: z.number(),
    removedFromCohorts: z.number(),
  })
  .passthrough()
const OrganizationSortFieldEnum = z.enum([
  'id',
  'name',
  'type',
  'active',
  'createdAt',
  'updatedAt',
])
const SortDirectionEnum = z.enum(['asc', 'desc'])

export const schemas = {
  CohortType,
  Cohort,
  CohortListResponse,
  ErrorResponse,
  CreateCohortRequest,
  AssignmentDetailsStatus,
  AssignmentDetailsQueryEntry,
  AssignmentDetailsQueries,
  AssignmentDetails,
  AssignmentStatus,
  AssignmentSettings,
  Assignment,
  CohortResponse,
  CohortPersonInput,
  AddPeopleToCohortRequest,
  AddPeopleToCohortResponse,
  RemovePeopleFromCohortRequest,
  RemovePeopleFromCohortResponse,
  UpdateCohortRequest,
  OrganizationType,
  queryFilters,
  OrganizationSettings,
  OrganizationAdminSettings,
  Organization,
  OrganizationListResponse,
  CreateOrganizationRequest,
  OrganizationResponse,
  OrganizationSearchResult,
  UpdateOrganizationRequest,
  UpdateOrganizationSettingsRequest,
  AssignmentListResponse,
  CreateAssignmentRequest,
  AssignmentResponse,
  UpdateAssignmentRequest,
  SetAssignmentStatusRequest,
  AssignmentDisclaimerResponse,
  VerifyLinkTokenResponse,
  CohortWithStats,
  PartnerDashboardResponse,
  AddPublicPersonRequest,
  AddPublicPersonResponse,
  AdminCleanupPersonRequest,
  AdminCleanupPersonResponse,
  OrganizationSortFieldEnum,
  SortDirectionEnum,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/admin/cleanup-person',
    alias: 'adminCleanupPerson',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AdminCleanupPersonRequest,
      },
    ],
    response: AdminCleanupPersonResponse,
  },
  {
    method: 'get',
    path: '/assignment-disclaimer',
    alias: 'getAssignmentDisclaimer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'assignmentId',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.object({ assignmentDisclaimer: z.string() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/assignments',
    alias: 'getAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'cohortId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'ownerId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['inactive', 'active']).optional(),
      },
    ],
    response: AssignmentListResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/assignments',
    alias: 'postAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAssignmentRequest,
      },
    ],
    response: AssignmentResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/assignments/:assignmentId',
    alias: 'getAssignmentsAssignmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'assignmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AssignmentResponse,
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/assignments/:assignmentId',
    alias: 'putAssignmentsAssignmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateAssignmentRequest,
      },
      {
        name: 'assignmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AssignmentResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/assignments/:assignmentId',
    alias: 'deleteAssignmentsAssignmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'assignmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/assignments/:assignmentId/increment-submissions',
    alias: 'postAssignmentsAssignmentIdincrementSubmissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'assignmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AssignmentResponse,
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'patch',
    path: '/assignments/:assignmentId/status',
    alias: 'patchAssignmentsAssignmentIdstatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetAssignmentStatusRequest,
      },
      {
        name: 'assignmentId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: AssignmentResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/assignments/add-public-person',
    alias: 'postAssignmentsaddPublicPerson',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPublicPersonRequest,
      },
    ],
    response: z.object({ success: z.boolean() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/assignments/verify-link-token',
    alias: 'getAssignmentsverifyLinkToken',
    requestFormat: 'json',
    parameters: [
      {
        name: 'token',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'personId',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: VerifyLinkTokenResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 409,
        description: `Person has already joined this public assignment`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/cohorts',
    alias: 'getCohorts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'isActive',
        type: 'Query',
        schema: z.boolean().optional(),
      },
    ],
    response: CohortListResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/cohorts',
    alias: 'postCohorts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCohortRequest,
      },
    ],
    response: CohortResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/cohorts/:cohortId',
    alias: 'getCohortsCohortId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'cohortId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CohortResponse,
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/cohorts/:cohortId',
    alias: 'putCohortsCohortId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCohortRequest,
      },
      {
        name: 'cohortId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CohortResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/cohorts/:cohortId',
    alias: 'deleteCohortsCohortId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'cohortId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/cohorts/add-people',
    alias: 'postCohortsaddPeople',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPeopleToCohortRequest,
      },
    ],
    response: AddPeopleToCohortResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/cohorts/remove-people',
    alias: 'postCohortsremovePeople',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePeopleFromCohortRequest,
      },
    ],
    response: RemovePeopleFromCohortResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/organizations',
    alias: 'getOrganizations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum(['id', 'name', 'type', 'active', 'createdAt', 'updatedAt'])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: OrganizationListResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/organizations',
    alias: 'postOrganizations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateOrganizationRequest,
      },
    ],
    response: OrganizationResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/organizations/:organizationId',
    alias: 'getOrganizationsOrganizationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganizationResponse,
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/organizations/:organizationId',
    alias: 'putOrganizationsOrganizationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateOrganizationRequest,
      },
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganizationResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/organizations/:organizationId',
    alias: 'deleteOrganizationsOrganizationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'patch',
    path: '/organizations/:organizationId/settings',
    alias: 'patchOrganizationsOrganizationIdsettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateOrganizationSettingsRequest,
      },
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganizationResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Requested resource not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/organizations/search',
    alias: 'getOrganizationssearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'q',
        type: 'Query',
        schema: z.string().min(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().lte(50).optional().default(20),
      },
    ],
    response: z.array(OrganizationSearchResult),
  },
  {
    method: 'get',
    path: '/partner-dashboard',
    alias: 'getPartnerDashboard',
    requestFormat: 'json',
    response: PartnerDashboardResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/partner-dashboard/admin/:organizationId',
    alias: 'getPartnerDashboardadminOrganizationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organizationId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: PartnerDashboardResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/person-assignments',
    alias: 'getPersonAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'personId',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: AssignmentListResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request payload or parameters`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
